import { useState, type CSSProperties, type KeyboardEvent, type ReactElement } from 'react';

import {
  activateSkill,
  containedPointerHandlers,
  describeSkill,
  layoutSkillTrees,
  type PlacedSkill,
  type SkillLink,
  type SkillTreeLayout,
  type SkillTreePanelProps,
} from './skill-tree-model';

export {
  type SkillTreePanelProps,
  type SkillTreeView,
  type TreeSkillView,
} from './skill-tree-model';

/**
 * The skill tree, as a player sees it.
 *
 * ## It renders a decision, it does not make one
 *
 * Every gate — character level, prerequisites, the point cost — has already
 * been resolved by the caller and arrives on `allocatable` and `lockedReason`.
 * This file lays out a grid, draws lines between cells and emits `onAllocate`;
 * `./skill-tree-model.ts` owns the rest and explains why the two blocks it
 * does apply (an empty pool, a skill at its maximum) are not rules.
 *
 * ## Sized for ninety skills, not for nine
 *
 * `docs/GAME-DESIGN.md` commits to three trees of thirty per class. Nine exist
 * today, and a layout tuned to nine would be thrown away on the day the
 * content lands — so the grid takes its row and column counts from the data
 * and is proven against a generated thirty-skill tree.
 *
 * ## It floats over a live game
 *
 * The canvas underneath is click-to-move. A pointer event that escapes this
 * panel sends the character walking, and a keystroke that escapes it fires a
 * hotkey, so the overlay swallows both at its edge.
 *
 * Styling is inline and local on purpose. The panels being built alongside this
 * one need the same palette, and a shared style module written twice by two
 * agents at once is a merge conflict rather than a saving; reconciling them is
 * a later, deliberate step.
 */

const PARCHMENT = '#d8d2c4';
const INK = '#0b0b0d';
const PANEL = '#15151a';
const EDGE = '#3a3730';
const GOLD = '#c8a44c';
const DIM = '#6f6a5e';

const OVERLAY: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  boxSizing: 'border-box',
  padding: '1rem',
  background: 'rgba(11, 11, 13, 0.92)',
  color: PARCHMENT,
  // Explicit, and asserted: `none` here would hand every click to the canvas
  // beneath and send the character walking to wherever the panel was clicked.
  pointerEvents: 'auto',
  overflow: 'auto',
};

const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', gap: '1rem' };
const TITLE: CSSProperties = { margin: 0, fontSize: '1rem', letterSpacing: '0.3em', textTransform: 'uppercase' };
const POINTS: CSSProperties = { marginLeft: 'auto', color: GOLD, fontWeight: 600 };
const TABLIST: CSSProperties = { display: 'flex', gap: '0.5rem' };
const GRID_WRAPPER: CSSProperties = { position: 'relative', flex: 1 };
/**
 * The prerequisite lines, and nothing else — least of all a click.
 *
 * It is absolutely positioned over the whole grid, so it paints above the cells
 * (a positioned box paints above in-flow content) and an `<svg>` root takes
 * pointer events across its entire box, not only where something is drawn. That
 * made **every cell in the tree unclickable in a real browser**, while a suite
 * with no layout engine went on passing: happy-dom cannot hit-test, so
 * `fireEvent.click` on the button reached the button whatever was over it.
 * `e2e/skills.spec.ts` is what found it, on 2026-08-13.
 *
 * `none` rather than reordering the layer, because the lines have to be *over*
 * the cells to be drawn between them, and because this layer is `aria-hidden`
 * decoration: there is nothing in it a pointer could ever want.
 */
const LINK_LAYER: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
};
const STATUS: CSSProperties = { minHeight: '1.25rem', color: GOLD, fontSize: '0.85rem' };

const tabStyle = (selected: boolean): CSSProperties => ({
  padding: '0.4rem 0.9rem',
  border: `1px solid ${selected ? GOLD : EDGE}`,
  borderRadius: 3,
  background: selected ? EDGE : 'transparent',
  color: selected ? PARCHMENT : DIM,
  font: 'inherit',
  cursor: 'pointer',
});

const cellStyle = (placed: PlacedSkill): CSSProperties => ({
  gridRow: placed.tier,
  gridColumn: placed.column,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
  padding: '0.5rem',
  border: `1px solid ${placed.spendable ? GOLD : EDGE}`,
  borderRadius: 3,
  background: PANEL,
  color: placed.spendable ? PARCHMENT : DIM,
  font: 'inherit',
  textAlign: 'left',
  cursor: placed.spendable ? 'pointer' : 'not-allowed',
});

const gridStyle = (layout: SkillTreeLayout): CSSProperties => ({
  position: 'relative',
  display: 'grid',
  gridTemplateRows: `repeat(${Math.max(layout.tierCount, 1)}, minmax(5rem, auto))`,
  gridTemplateColumns: `repeat(${Math.max(layout.columnCount, 1)}, minmax(8rem, 1fr))`,
  gap: '0.75rem',
});

/** One prerequisite line, drawn centre to centre in grid units. */
const renderLink = (link: SkillLink): ReactElement => (
  <line
    key={`${link.fromSkillId}->${link.toSkillId}`}
    data-skill-link={`${link.fromSkillId}->${link.toSkillId}`}
    x1={link.fromColumn - 0.5}
    y1={link.fromTier - 0.5}
    x2={link.toColumn - 0.5}
    y2={link.toTier - 0.5}
    stroke={GOLD}
    strokeWidth={1}
    strokeOpacity={0.45}
    vectorEffect="non-scaling-stroke"
  />
);

/**
 * The link layer.
 *
 * The `viewBox` is the grid itself — one unit per cell — so a line's endpoints
 * are just the cell coordinates and no pixel measuring is needed. Hidden from
 * assistive technology because the same information reaches a screen reader as
 * words, in each skill's accessible name.
 */
const renderLinks = (layout: SkillTreeLayout): ReactElement => (
  <svg
    aria-hidden="true"
    style={LINK_LAYER}
    viewBox={`0 0 ${Math.max(layout.columnCount, 1)} ${Math.max(layout.tierCount, 1)}`}
    preserveAspectRatio="none"
  >
    {layout.links.map(renderLink)}
  </svg>
);

const renderCell = (placed: PlacedSkill, onActivate: (placed: PlacedSkill) => void): ReactElement => (
  <button
    key={placed.skill.id}
    type="button"
    data-skill-id={placed.skill.id}
    // `aria-disabled`, not `disabled`: a locked skill stays focusable and
    // answers a click with the reason it is locked. A `disabled` button fires
    // no click at all, so it could only ever say nothing.
    aria-disabled={!placed.spendable}
    aria-label={describeSkill(placed)}
    style={cellStyle(placed)}
    onClick={(): void => { onActivate(placed); }}
  >
    <span style={{ display: 'flex', gap: '0.5rem', fontWeight: 600 }}>
      <span>{placed.skill.name}</span>
      <span style={{ marginLeft: 'auto', color: GOLD }}>
        {placed.skill.points}/{placed.skill.maximumPoints}
      </span>
    </span>
    <span style={{ fontSize: '0.75rem', color: DIM }}>Level {placed.skill.requiredLevel}</span>
    <span style={{ fontSize: '0.75rem' }}>{placed.skill.description}</span>
  </button>
);

/** A tab's accessible name is its spent total — what a D2 player navigates by. */
const renderTab = (
  layout: SkillTreeLayout,
  selected: boolean,
  onSelect: (treeId: string) => void,
): ReactElement => (
  <button
    key={layout.treeId}
    type="button"
    role="tab"
    aria-selected={selected}
    aria-label={`${layout.treeName} tree, ${layout.spentPoints} ${layout.spentPoints === 1 ? 'point' : 'points'} spent`}
    style={tabStyle(selected)}
    onClick={(): void => { onSelect(layout.treeId); }}
  >
    <span>{layout.treeName}</span> <span style={{ color: GOLD }}>{layout.spentPoints}</span>
  </button>
);

export const SkillTreePanel = ({
  trees,
  unspentSkillPoints,
  onAllocate,
  onClose,
}: SkillTreePanelProps): ReactElement => {
  const [selectedTreeId, setSelectedTreeId] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  const layouts = layoutSkillTrees(trees, unspentSkillPoints);
  // Falling back rather than remembering a dead id: a selected tree can vanish
  // when the caller re-renders with a different class, and no effect is needed
  // to notice.
  const active = layouts.find((layout) => layout.treeId === selectedTreeId) ?? layouts[0];

  const selectTree = (treeId: string): void => {
    setSelectedTreeId(treeId);
    setStatus('');
  };

  const activate = (placed: PlacedSkill): void => {
    const outcome = activateSkill(placed);
    if (outcome.kind === 'allocate') {
      setStatus('');
      onAllocate(outcome.skillId);
      return;
    }
    setStatus(outcome.reason);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // Stopped first and unconditionally: a keystroke that reaches the game
    // fires a hotkey, and quaffing a potion while reading a skill tree is not
    // something a player asked for.
    event.stopPropagation();
    if (event.key === 'Escape') onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Skills"
      style={OVERLAY}
      {...containedPointerHandlers()}
      onKeyDown={onKeyDown}
    >
      <div style={HEADER}>
        <h2 style={TITLE}>Skills</h2>
        <span style={POINTS} aria-label={`${unspentSkillPoints} unspent skill points`}>
          {unspentSkillPoints} unspent
        </span>
        <button
          type="button"
          aria-label="Close skill tree"
          style={{ ...tabStyle(false), color: PARCHMENT }}
          onClick={onClose}
        >
          Close
        </button>
      </div>

      <div role="tablist" aria-label="Skill trees" style={TABLIST}>
        {layouts.map((layout) => renderTab(layout, layout.treeId === active?.treeId, selectTree))}
      </div>

      <div style={GRID_WRAPPER}>
        {active === undefined ? null : (
          <div role="tabpanel" aria-label={`${active.treeName} skills`} style={gridStyle(active)}>
            {renderLinks(active)}
            {active.placed.map((placed) => renderCell(placed, activate))}
          </div>
        )}
      </div>

      <p role="status" aria-live="polite" style={{ ...STATUS, background: INK }}>
        {status}
      </p>
    </div>
  );
};
